import React from 'react';
import classnames from 'classnames';
import { compose } from 'recompose';
import { withTranslation, WithTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-types-papi';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import CameraIcon from '@wix/da-shared-react/pkg/Icons/Camera';
import PencilIcon from '@wix/da-shared-react/pkg/Icons/Pencil';
import TrashcanIcon from '@wix/da-shared-react/pkg/Icons/Trashcan';
import {
  BiEventType,
  BiUpdateType,
} from '@wix/da-shared-react/pkg/biLogger/constants';
import {
  withLogBiEvent,
  WithLogBiEventProps,
} from '@wix/da-shared-react/pkg/biLogger/hocs/withLogBiEvent';
import BiButton from '@wix/da-shared-react/pkg/biLogger/Components/BiButton';
import Button from '@wix/da-shared-react/pkg/Button';
import MediaSelector from '@wix/da-shared-react/pkg/MediaPopup/MediaSelector';
import { MediaSourceType } from '@wix/da-shared-react/pkg/MediaPopup/types';
import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import TextTooltipOnHover from '@wix/da-shared-react/pkg/Popover/TextTooltipOnHover';
import StandardMenu from '@wix/da-shared-react/pkg/Dropdown/Preset/StandardMenu';
import onClickOutside from 'react-onclickoutside';
import {
  PerspectiveContextProps,
  withPerspectiveContext,
} from '@wix/da-shared-react/pkg/Context/PerspectiveContext';
import {
  UpdateCoverDeviationPayload,
  UpdateCoverDeviationOffsetYPayload,
} from '../../../../actions/deviations';
import PositionManager from './PositionManager';
import { INITIAL_SCROLL_TOP } from '../ProfileHero';
import { isSourceImageVertical } from '@wix/da-shared-react/pkg/DeviationViews/Dumbo/Image/_helpers';
import { ModuleType } from '../../../../../types/modules';
import {
  withModuleConfig,
  WithModuleConfigProps,
} from '../../_hoc/withModuleConfig';

import s from './ProfileHeroEdit.scss';

const StandardMenuWithOnClickOutside = onClickOutside(StandardMenu);

const enhance = compose(
  withLogBiEvent,
  withPerspectiveContext,
  withModuleConfig,
  withTranslation()
);

export interface Props {
  moduleId: number;
  coverDeviation?: PapiDeviation;
  withPositionManager: boolean;
  updateCoverDeviation: (payload: UpdateCoverDeviationPayload) => void;
  updateCoverDeviationOffsetY: (
    payload: UpdateCoverDeviationOffsetYPayload
  ) => void;
  deleteCoverDeviation: () => void;
  openIntroPopup: () => void;
  dropProfileEditAidFlag?: () => void;
  className: string;
  showProfileHeroAid?: boolean;
  showProfileEditAid?: boolean;
  showWatchIntro?: boolean;
}

export interface State {
  showMenu: boolean;
  showMediaManager: boolean;
  showPositionManager: boolean;
}

export class ProfileHeroEdit extends React.Component<
  Props &
    PerspectiveContextProps &
    WithTranslation &
    WithLogBiEventProps &
    WithModuleConfigProps,
  State
> {
  state = {
    showMenu: false,
    showMediaManager: false,
    showPositionManager: false,
  };
  t = this.props.t!;

  render() {
    const {
      coverDeviation,
      className,
      showProfileHeroAid,
      showWatchIntro,
    } = this.props;
    return (
      <div
        className={classnames(
          s['root-wrapper'],
          showProfileHeroAid && s['is-ftue'],
          className
        )}
      >
        <div className={classnames(s['root'])}>
          {showWatchIntro && this.renderWatchIntroControl()}
          {coverDeviation
            ? this.renderControlsWhenCoverIsSet()
            : this.renderControlsWhenCoverIsNotSet()}
          {this.state.showMediaManager && (
            <MediaSelector
              mediaSourceTypes={[
                MediaSourceType.GALLERY,
                MediaSourceType.STASH,
              ]}
              allowedDeviationTypes={[DeviationTypes.IMAGE]}
              allowGifs={false}
              onClose={this.hideMediaManager}
              onSelect={this.updateCoverDeviation}
            />
          )}
          {this.state.showPositionManager && (
            <PositionManager
              onOpenMediaManager={this.onOpenMediaManagerFromPositionManager}
              onSubmit={this.onPositionManagerSubmit}
              onClose={this.hidePositionMananger}
            />
          )}
        </div>
      </div>
    );
  }

  renderControlsWhenCoverIsSet = () => {
    const {
      deleteCoverDeviation,
      withPositionManager,
      showProfileHeroAid,
      t,
    } = this.props;
    return (
      <>
        <span className={s['delete-btn-wrapper']}>
          <TextTooltipOnHover
            tooltipText={this.t('common.profileHero.delete')}
            tooltipOffset={'0, 4'}
            renderReference={({ ref }) => (
              <BiButton
                key="delete-btn"
                className={classnames(s['icon-btn'], s['delete-btn'])}
                onClick={deleteCoverDeviation}
                data-role={showProfileHeroAid ? 'ftue-control' : 'edit-control'}
                biData={{
                  evid: BiEventType.REMOVE_WIDGET_CLICK,
                  widgetname: ModuleType.COVER_DEVIATION,
                }}
                aria-label={t('common.profileHero.a11y.removeCoverImage')}
              >
                <TrashcanIcon
                  className={s['icon']}
                  size={IconSize.MEDIUM}
                  containerRef={ref}
                  condensed
                />
              </BiButton>
            )}
          />
        </span>
        {withPositionManager
          ? this.renderEditCoverButton()
          : this.renderChangeImageButton()}
      </>
    );
  };

  renderEditCoverButton = () => {
    return (
      <BiButton
        key="change-btn"
        className={s['icon-btn']}
        onClick={this.showMenu}
        data-role={
          this.props.showProfileHeroAid ? 'ftue-control' : 'edit-control'
        }
      >
        <span className={s['icon-btn-content']}>
          <PencilIcon className={s['icon']} size={IconSize.MEDIUM} condensed />
          <span className={s['btn-title']}>
            {this.t('common.profileHero.editCoverImage')}
          </span>
        </span>
        {this.state.showMenu && (
          <StandardMenuWithOnClickOutside
            className={s['menu']}
            items={[
              {
                label: this.t('common.profileHero.chooseCoverImage'),
                onClick: this.showMediaManager,
                componentProps: {
                  biData: {
                    evid: BiEventType.PROFILE_COVER_CLICK,
                    version: 'eclipse',
                    is_update: BiUpdateType.CHANGE,
                  },
                },
              },
              {
                label: this.t('common.profileHero.adjustPosition'),
                onClick: this.showPositionMananger,
                componentProps: {
                  biData: {
                    evid: BiEventType.ADJUST_COVER_POSITION,
                    version: 'eclipse',
                    widgetname: 'cover_deviation',
                  },
                },
              },
            ]}
            handleClickOutside={this.hideMenu}
          />
        )}
      </BiButton>
    );
  };

  renderChangeImageButton = () => (
    <div className={s['icon-btn-wrapper']}>
      <Button
        key="change-btn"
        className={s['icon-btn']}
        onClick={this.showMediaManager}
        data-role={
          this.props.showProfileHeroAid ? 'ftue-control' : 'edit-control'
        }
        view="b8"
        biData={{
          evid: BiEventType.PROFILE_COVER_CLICK,
          is_update: BiUpdateType.CHANGE,
        }}
        aria-label={this.props.t('common.profileHero.editCoverImage')}
      >
        <span className={s['icon-btn-content']}>
          <PencilIcon className={s['icon']} size={IconSize.MEDIUM} condensed />
          <span className={s['btn-title']}>
            {this.t!('common.profileHero.editCoverImage')}
          </span>
        </span>
      </Button>
    </div>
  );

  renderControlsWhenCoverIsNotSet = () => {
    const { t, showProfileHeroAid } = this.props;
    return (
      <TextTooltipOnHover
        tooltipText={t('common.profileHero.addCoverImage.tooltip')}
        className={s['icon-btn-wrapper']}
        contentClassName={s['ftue-tooltip-content']}
      >
        <Button
          key="add-btn"
          className={s['icon-btn']}
          onClick={this.showMediaManager}
          data-role={showProfileHeroAid ? 'ftue-control' : 'edit-control'}
          view="b8"
          biData={{
            evid: BiEventType.PROFILE_COVER_CLICK,
            is_update: BiUpdateType.ADD,
          }}
        >
          <span className={s['icon-btn-content']}>
            <CameraIcon className={s['icon']} size={IconSize.SMALL} condensed />
            <span className={s['btn-title']}>
              {t!('common.profileHero.addCoverImage')}
            </span>
          </span>
        </Button>
        {showProfileHeroAid && <div className={s['glow']} />}
      </TextTooltipOnHover>
    );
  };

  renderWatchIntroControl = () => {
    const { openIntroPopup, dropProfileEditAidFlag, t } = this.props;
    return (
      <TextTooltipOnHover
        className={classnames(s['icon-btn-wrapper'], s['watch-intro-control'])}
        tooltipText={t('common.profileHero.watchIntro.tooltip')}
        renderReference={({ ref }) => (
          <Button
            view="b34"
            height="medium"
            data-role="edit-control"
            onClick={() => {
              openIntroPopup && openIntroPopup();
              dropProfileEditAidFlag && dropProfileEditAidFlag();
            }}
          >
            <span ref={ref}>{t('common.profileHero.empty.watchIntro')}</span>
          </Button>
        )}
      />
    );
  };

  showMenu = () => {
    const { logBiEvent } = this.props;
    if (!this.state.showMenu) {
      logBiEvent({
        evid: BiEventType.EDIT_WIDGET_CLICK,
        widgetname: 'cover_deviation',
        version: 'eclipse',
      });
    }
    this.setState({ showMenu: true });
  };

  hideMenu = () => {
    this.setState({ showMenu: false });
  };

  showPositionMananger = () => {
    this.setState({ showPositionManager: true });
  };

  hidePositionMananger = () => {
    this.setState({ showPositionManager: false });
  };

  scrollToInitPosition = () => {
    const { getScrollableContainer } = this.props;
    const scrollableContainer = getScrollableContainer();
    scrollableContainer && scrollableContainer.scrollTo(0, INITIAL_SCROLL_TOP);
  };

  onPositionManagerSubmit = offsetY => {
    const { updateCoverDeviationOffsetY, moduleConfig } = this.props;
    updateCoverDeviationOffsetY({
      offsetY,
      version: moduleConfig?.config?.version,
    });
    this.scrollToInitPosition();
    this.hidePositionMananger();
  };

  onOpenMediaManagerFromPositionManager = () => {
    this.showMediaManager();
    this.hidePositionMananger();
  };

  showMediaManager = () => {
    this.setState({ showMediaManager: true });
  };

  hideMediaManager = () => {
    this.setState({ showMediaManager: false });
  };

  updateCoverDeviation = items => {
    const { moduleConfig } = this.props;
    const selectedDeviation = items[0];
    if (selectedDeviation) {
      this.props.updateCoverDeviation({
        deviation: selectedDeviation,
        version: moduleConfig?.config?.version,
      });
      this.scrollToInitPosition();
      this.hideMediaManager();
      if (isSourceImageVertical(selectedDeviation)) {
        this.showPositionMananger();
      }
    }
  };
}

export default enhance(ProfileHeroEdit) as React.ComponentType<Props>;
